import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Clock, Landmark, Info } from "lucide-react";
import { useState } from "react";
import { useApp } from "../context/AppContext";
import Avatar from "../components/Avatar";
import ScreenHeader from "../components/ScreenHeader";
import PartnerBadge from "../components/PartnerBadge";

export const ConfirmationScreen = () => {
  const nav = useNavigate();
  const { draft, computeFee, partner, createTransaction } = useApp();
  const [submitting, setSubmitting] = useState(false);
  const recipient = draft.recipient;
  const amount = Number(draft.amount) || 0;

  if (!recipient || amount <= 0) {
    nav("/", { replace: true });
    return null;
  }

  const fee = computeFee(amount);
  const recipientGets = Math.round((amount - fee) * 100) / 100;

  const onConfirm = () => {
    if (submitting) return;
    setSubmitting(true);
    createTransaction();
    nav("/send/auth");
  };

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ type: "spring", bounce: 0, duration: 0.35 }}
      className="h-full flex flex-col"
      data-testid="confirmation-screen"
    >
      <ScreenHeader title="Review & confirm" testId="confirm-back-btn" />

      <div className="px-6 pb-3">
        <h1 className="font-display text-[26px] font-bold leading-tight tracking-tight dark:text-white">
          You're sending
          <br />
          <span className="text-gray-500 dark:text-gray-400">to {recipient.name}</span>
        </h1>
      </div>

      {/* Big amount card */}
      <div className="px-6 mt-2">
        <div className="rounded-3xl border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-5">
          <div className="flex items-center gap-3 mb-4">
            <Avatar initials={recipient.initials} color={recipient.color} size={44} />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold">
                Recipient
              </div>
              <div className="text-[14px] font-semibold text-gray-900 dark:text-white truncate">
                {recipient.name}
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{recipient.phone}</div>
            </div>
          </div>

          <div className="flex items-end justify-between pb-3 border-b border-gray-200">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold">
                You send
              </div>
              <div className="font-display text-[36px] font-bold leading-none mt-1 text-gray-900 dark:text-white">
                {amount.toFixed(2)} kr
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold">
                They receive
              </div>
              <div data-testid="confirm-recipient-gets" className="font-display text-[22px] font-bold text-emerald-600 mt-1 leading-none">
                {recipientGets.toFixed(2)} kr
              </div>
            </div>
          </div>

          <div className="pt-3 space-y-2.5 text-[13px]">
            <Row label="Fees" value={`${fee.toFixed(2)} kr`} testId="confirm-fee" />
            <Row
              label="Payment method"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <Landmark className="w-3.5 h-3.5 text-gray-500" strokeWidth={2.2} />
                  Bank Transfer
                </span>
              }
            />
            <Row
              label="Estimated arrival"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-gray-500" strokeWidth={2.2} />
                  ~30 seconds
                </span>
              }
            />
          </div>
        </div>
      </div>

      {/* Compliance banner */}
      <div className="px-6 mt-4">
        <PartnerBadge partner={partner.name} variant="banner" testId="confirm-partner-banner" />
      </div>

      <div className="px-6 mt-3 flex items-start gap-2 text-[11px] text-gray-500 leading-snug">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2.2} />
        <span>
          Shwiip provides the interface only. Funds never touch Shwiip accounts — they
          are routed and settled directly by <b>{partner.name}</b>.
        </span>
      </div>

      <div className="mt-auto p-6 pt-4">
        <button
          data-testid="confirm-pay-btn"
          onClick={onConfirm}
          disabled={submitting}
          className="w-full bg-gray-900 dark:bg-emerald-600 text-white h-14 rounded-full font-semibold text-[15px] hover:bg-black dark:hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:bg-gray-400 dark:disabled:bg-white/10 disabled:cursor-not-allowed"
        >
          {submitting ? "Processing…" : "Confirm & continue"}
        </button>
      </div>
    </motion.div>
  );
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span data-testid={testId} className="font-semibold text-gray-900 dark:text-white">
      {value}
    </span>
  </div>
);

export default ConfirmationScreen;
